/**
 * OperationalLedger — collapsed replacement for the three stacked sections
 * (Cash Movement, Labor & Adjustments, Operating Costs Booked).
 *
 * Design principles:
 *   • One section header, not three. The Command Center already owns altitude.
 *   • Zero-value cards are HIDDEN, not rendered as visual noise.
 *   • Operating Costs render as a sorted horizontal bar list — weight follows dollars.
 *   • The transaction table below is the hero; this band is a quiet summary.
 */
import { Link } from "react-router-dom";
import { CockpitSection } from "@/components/cockpit/CockpitShell";

const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;

export interface OperationalLedgerProps {
  // Cash movement
  readyAmount: number;
  readyCount: number;
  heldAmount: number;
  heldCount: number;
  pendingFieldAddAmount: number;
  pendingFieldAddCount: number;
  releasedMtdAmount: number;
  releasedMtdCount: number;
  // Variance / risk
  varianceMeasurable: boolean;
  laborDeltaPct: number;
  laborJobsWithActuals: number;
  laborTotalJobs: number;
  approvedFieldAddTotal: number;
  approvedFieldAddJobs: number;
  holdbackAmount: number;
  holdbackCount: number;
  openDisputeCount: number;
  // Expense ledger
  ledgerAuthorized: boolean;
  ledgerRows: Array<[string, { amount: number; count: number }]>;
  ledgerTotal: number;
  ledgerEntryCount: number;
  categoryLabel: (cat: string) => string;
}

type Signal = {
  key: string;
  label: string;
  value: string;
  detail: string;
  tone: "neutral" | "warning" | "danger" | "success";
  to?: string;
};

export function OperationalLedger(p: OperationalLedgerProps) {
  // Build the signal strip — include only items that carry information.
  // Released MTD always shows (anchor for the period).
  const signals: Signal[] = [];

  if (p.heldAmount > 0) {
    signals.push({
      key: "held",
      label: "Held",
      value: fmt(p.heldAmount),
      detail: `${p.heldCount} blocked`,
      tone: "warning",
      to: "/finance/pay-runs?pivot=held",
    });
  }
  if (p.readyAmount > 0) {
    signals.push({
      key: "ready",
      label: "Ready to Release",
      value: fmt(p.readyAmount),
      detail: `${p.readyCount} pay run${p.readyCount === 1 ? "" : "s"}`,
      tone: "success",
      to: "/finance/pay-runs",
    });
  }
  if (p.pendingFieldAddAmount > 0) {
    signals.push({
      key: "pending",
      label: "Pending Approvals",
      value: fmt(p.pendingFieldAddAmount),
      detail: `${p.pendingFieldAddCount} field-add${p.pendingFieldAddCount === 1 ? "" : "s"}`,
      tone: "warning",
      to: "/finance/pay-runs?pivot=approvals",
    });
  }
  if (p.holdbackAmount > 0) {
    signals.push({
      key: "holdback",
      label: "Active Holdbacks",
      value: fmt(p.holdbackAmount),
      detail: `${p.holdbackCount} held`,
      tone: "warning",
      to: "/execution/holdbacks",
    });
  }
  if (p.openDisputeCount > 0) {
    signals.push({
      key: "disputes",
      label: "Open Disputes",
      value: String(p.openDisputeCount),
      detail: "Pauses release",
      tone: "danger",
      to: "/execution/holdbacks",
    });
  }
  if (p.approvedFieldAddTotal > 0) {
    signals.push({
      key: "approved-fa",
      label: "Approved Field-Adds",
      value: fmt(p.approvedFieldAddTotal),
      detail: `${p.approvedFieldAddJobs} job${p.approvedFieldAddJobs === 1 ? "" : "s"} · raises payout`,
      tone: "neutral",
    });
  }
  // Released MTD — always anchor
  signals.push({
    key: "released",
    label: "Released · MTD",
    value: fmt(p.releasedMtdAmount),
    detail: `${p.releasedMtdCount} pay run${p.releasedMtdCount === 1 ? "" : "s"}`,
    tone: "neutral",
    to: "/finance/pay-runs?pivot=paid",
  });

  // Variance line — its own muted row beneath the signals
  const varianceLine = p.varianceMeasurable
    ? `Labor variance ${p.laborDeltaPct >= 0 ? "+" : ""}${p.laborDeltaPct.toFixed(1)}% · ${p.laborJobsWithActuals}/${p.laborTotalJobs} jobs reconciled`
    : `Labor variance — awaiting actuals (0/${p.laborTotalJobs} jobs reconciled)`;

  // Operating costs: sorted, bar-weighted
  const sortedLedger = [...p.ledgerRows].sort((a, b) => b[1].amount - a[1].amount);
  const ledgerMax = sortedLedger.reduce((m, [, d]) => Math.max(m, d.amount), 0);

  const toneClass = (t: Signal["tone"]) => {
    switch (t) {
      case "warning":
        return "text-warning";
      case "danger":
        return "text-destructive";
      case "success":
        return "text-success";
      default:
        return "text-foreground";
    }
  };

  return (
    <CockpitSection
      eyebrow="Pay Run · Variance · Expense Ledger"
      title="Operational Ledger"
      actions={
        <Link to="/finance/pay-runs" className="text-[11px] text-primary hover:underline">
          Open Pay Runs →
        </Link>
      }
    >
      {/* Signal strip — only non-zero items + released anchor */}
      <div className="border border-border/60 bg-background">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 divide-x divide-y md:divide-y-0 divide-border/60">
          {signals.map((s) => {
            const inner = (
              <div className="px-4 py-3">
                <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-1">
                  {s.label}
                </p>
                <p className={`text-base font-semibold tabular-nums ${toneClass(s.tone)}`}>{s.value}</p>
                <p className="text-[11px] text-muted-foreground tabular-nums mt-0.5">{s.detail}</p>
              </div>
            );
            return s.to ? (
              <Link key={s.key} to={s.to} className="hover:bg-muted/30 transition-colors">
                {inner}
              </Link>
            ) : (
              <div key={s.key}>{inner}</div>
            );
          })}
        </div>

        {/* Variance line — muted, single line, no card chrome */}
        <div className="border-t border-border/60 px-4 py-2 flex items-center justify-between">
          <p className="text-[11px] text-muted-foreground">
            <span className="uppercase tracking-[0.14em] mr-2">Reconciliation</span>
            {varianceLine}
          </p>
          {p.varianceMeasurable && p.laborJobsWithActuals < p.laborTotalJobs && (
            <Link to="/finance/pay-runs?pivot=variance" className="text-[11px] text-primary hover:underline">
              Variance detail →
            </Link>
          )}
        </div>
      </div>

      {/* Operating Costs — sorted bar list */}
      {p.ledgerAuthorized && (
        <div className="mt-4 border border-border/60 bg-background">
          <div className="px-4 py-2.5 border-b border-border/60 flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">
              Operating Costs Booked
            </p>
            <p className="text-[11px] text-muted-foreground tabular-nums">
              {p.ledgerEntryCount} entr{p.ledgerEntryCount === 1 ? "y" : "ies"} · {fmt(p.ledgerTotal)}
            </p>
          </div>
          {sortedLedger.length === 0 ? (
            <p className="text-xs text-muted-foreground px-4 py-5">
              No realized expense entries for the current filter.
            </p>
          ) : (
            <div className="divide-y divide-border/60">
              {sortedLedger.map(([cat, data]) => {
                const pct = ledgerMax > 0 ? (data.amount / ledgerMax) * 100 : 0;
                return (
                  <div key={cat} className="px-4 py-2 grid grid-cols-[180px_1fr_auto] items-center gap-3">
                    <p className="text-[12px] text-foreground truncate">{p.categoryLabel(cat)}</p>
                    <div className="h-1.5 bg-muted/40 rounded-sm overflow-hidden">
                      <div
                        className="h-full bg-primary/60"
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      />
                    </div>
                    <p className="text-[12px] tabular-nums text-foreground font-medium w-[120px] text-right">
                      {fmt(data.amount)}{" "}
                      <span className="text-muted-foreground font-normal">
                        · {data.count}
                      </span>
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </CockpitSection>
  );
}
